import React, { useState, useEffect } from 'react';
import { cn } from '../lib/cn';
import { apiKeyService, KeyStorage, AIProvider, PROVIDERS, ModelOption } from '../services/apiKeyService';
import { useT } from '../lib/i18n';

export const AdminPanel: React.FC = () => {
  const { t } = useT();
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[880px] mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-1.5 tracking-tight">{t('admin.title')}</h1>
        <p className="text-text-muted text-sm sm:text-[15px]">
          {t('admin.subtitle')}
        </p>
      </div>

      <AiSettings />
    </div>
  );
};

/* ─────────────────────────── AI Settings ─────────────────────────── */

const AiSettings: React.FC = () => {
  const { t } = useT();
  const [activeProvider, setActiveProvider] = useState<AIProvider>(apiKeyService.getActiveProvider());

  const handleProviderChange = (provider: AIProvider) => {
    setActiveProvider(provider);
    apiKeyService.setActiveProvider(provider);
  };

  return (
    <div className="space-y-5">
      {/* Active Provider Selector */}
          <div className="p-5 bg-surface-2 border border-border rounded-xl">
            <h3 className="text-[15px] font-semibold text-text-primary mb-3">{t('admin.activeProvider')}</h3>
            <div className="grid grid-cols-3 gap-2">
              {(Object.keys(PROVIDERS) as AIProvider[]).map(id => {
                const config = PROVIDERS[id];
                const hasKey = apiKeyService.hasKey(id);
                return (
                  <button
                    key={id}
                    onClick={() => handleProviderChange(id)}
                    className={cn(
                      'p-3 rounded-lg border text-center transition-all duration-150 cursor-pointer',
                      activeProvider === id
                        ? 'border-brand bg-brand/5 ring-1 ring-brand/30'
                        : 'border-border bg-surface-1 hover:border-border-hover'
                    )}
                  >
                    <div className="text-[13px] font-semibold text-text-primary">{config.name}</div>
                    <div className={cn(
                      'text-[11px] mt-1 font-medium',
                      hasKey ? 'text-success' : 'text-text-faint'
                    )}>
                      {hasKey ? t('admin.configured') : t('admin.notConfigured')}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-text-faint mt-3 m-0">
              {t('admin.providerNote')}
            </p>
          </div>

          {/* Model Selector */}
          <ModelSelector provider={activeProvider} />

          {/* Selected Provider Key Card */}
          <ProviderKeyCard key={activeProvider} provider={activeProvider} isActive={true} />

      {/* How It Works */}
      <div className="p-5 bg-surface-2 border border-border rounded-xl">
        <h3 className="text-[15px] font-semibold text-text-primary mb-3">{t('admin.howItWorks')}</h3>
        <div className="space-y-2.5">
          {[
            t('admin.howStep1'),
            t('admin.howStep2'),
            t('admin.howStep3'),
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-brand-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-brand text-[11px] font-bold">{i + 1}</span>
              </div>
              <p className="text-[13px] text-text-secondary m-0 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── Provider Key Card ─────────────────────── */

const ProviderKeyCard: React.FC<{ provider: AIProvider; isActive: boolean }> = ({ provider, isActive }) => {
  const { t } = useT();
  const config = PROVIDERS[provider];

  const [keyInput, setKeyInput] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [hasKey, setHasKey] = useState(apiKeyService.hasKey(provider));
  const [maskedKey, setMaskedKey] = useState('');
  const [storageType, setStorageType] = useState<KeyStorage>('local');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const key = apiKeyService.getKey(provider);
    if (key) {
      setMaskedKey(apiKeyService.maskKey(key));
      setStorageType(apiKeyService.getStorageType(provider) || 'local');
    }
    setHasKey(!!key);
  }, [provider]);

  const handleSave = () => {
    setValidationError(null);
    setSaveStatus('idle');

    if (!keyInput.trim()) {
      setValidationError(t('admin.enterApiKey'));
      return;
    }

    if (!apiKeyService.validateKeyFormat(keyInput.trim(), provider)) {
      setValidationError(t('admin.invalidKeyFormat', { name: config.name, prefix: config.keyPrefix }));
      return;
    }

    apiKeyService.setKey(keyInput.trim(), storageType, provider);
    setHasKey(true);
    setMaskedKey(apiKeyService.maskKey(keyInput.trim()));
    setKeyInput('');
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  const handleRemove = () => {
    apiKeyService.clearKey(provider);
    setHasKey(false);
    setMaskedKey('');
    setKeyInput('');
  };

  const isConnected = hasKey;

  return (
    <div className={cn(
      'p-5 bg-surface-2 border rounded-xl transition-all duration-150',
      isActive ? 'border-brand/40' : 'border-border'
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h3 className="text-[15px] font-semibold text-text-primary">{config.name}</h3>
          {isActive && isConnected && (
            <span className="text-[10px] text-brand bg-brand/10 px-1.5 py-0.5 rounded font-medium">{t('admin.inUse')}</span>
          )}
          {isActive && !isConnected && (
            <span className="text-[10px] text-warning bg-warning/10 px-1.5 py-0.5 rounded font-medium">{t('admin.notConfigured')}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {config.capabilities.map(cap => (
              <span key={cap} className="text-[10px] text-text-faint bg-surface-3 px-1.5 py-0.5 rounded">
                {cap === 'chat' ? t('admin.textGeneration') : t('admin.speechRecognition')}
              </span>
            ))}
          </div>
          {isConnected && (
            <span className="flex items-center gap-1 text-success text-[11px] font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-success" />
              {t('admin.configured')}
            </span>
          )}
        </div>
      </div>

      {hasKey ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-3 bg-surface-1 rounded-lg">
              <code className="flex-1 text-[13px] text-text-secondary font-mono tracking-wider">
                {maskedKey}
              </code>
              <button
                onClick={handleRemove}
                className="px-3 py-1.5 text-[12px] text-danger bg-danger-muted border-none rounded-lg cursor-pointer font-medium hover:bg-danger/20 transition-colors"
              >
                {t('common.delete')}
              </button>
            </div>
            <p className="text-[11px] text-text-faint m-0">
              {t('admin.storageLabel')} {apiKeyService.getStorageType(provider) === 'local' ? t('admin.storagePersistent') : t('admin.storageSession')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={keyInput}
                  onChange={(e) => { setKeyInput(e.target.value); setValidationError(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  placeholder={config.keyPlaceholder}
                  className="w-full py-2.5 px-3 pr-16 bg-surface-1 border border-border rounded-lg text-text-primary text-[13px] font-mono placeholder:text-text-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:border-brand/50 transition-all"
                />
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-[11px] text-text-muted bg-transparent border-none cursor-pointer hover:text-text-secondary"
                >
                  {showKey ? t('common.hide') : t('common.show')}
                </button>
              </div>
              <button
                onClick={handleSave}
                className={cn(
                  'px-5 py-2.5 rounded-lg text-[13px] font-semibold border-none cursor-pointer transition-all duration-200',
                  saveStatus === 'saved'
                    ? 'bg-success-muted text-success'
                    : 'bg-brand text-white hover:bg-brand-hover'
                )}
              >
                {saveStatus === 'saved' ? t('common.saved') : t('common.save')}
              </button>
            </div>

            {validationError && (
              <p className="text-danger text-[12px] m-0">{validationError}</p>
            )}

            {/* Storage Type Toggle */}
            <div className="flex items-center gap-3">
              <span className="text-[12px] text-text-muted">{t('admin.storageLabel')}</span>
              <div className="flex gap-1 bg-surface-1 rounded-lg p-0.5">
                <button
                  onClick={() => setStorageType('session')}
                  className={cn(
                    'px-3 py-1 rounded-md text-[11px] font-medium transition-all duration-150 border-none cursor-pointer',
                    storageType === 'session'
                      ? 'bg-surface-3 text-text-primary shadow-sm'
                      : 'bg-transparent text-text-muted hover:text-text-secondary'
                  )}
                >
                  {t('admin.sessionOnly')}
                </button>
                <button
                  onClick={() => setStorageType('local')}
                  className={cn(
                    'px-3 py-1 rounded-md text-[11px] font-medium transition-all duration-150 border-none cursor-pointer',
                    storageType === 'local'
                      ? 'bg-surface-3 text-text-primary shadow-sm'
                      : 'bg-transparent text-text-muted hover:text-text-secondary'
                  )}
                >
                  {t('admin.persistent')}
                </button>
              </div>
            </div>

            <p className="text-[11px] text-text-faint leading-relaxed m-0">
              {t('admin.getKeyFrom')}{' '}
              <a
                href={config.keyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:underline"
              >
                {config.keyUrlLabel}
              </a>
            </p>
          </div>
        )}
    </div>
  );
};

/* ─────────────────────── Model Selector ─────────────────────── */

const tierBadge: Record<ModelOption['tier'], { label: string; color: string }> = {
  free: { label: '$', color: 'text-success' },
  standard: { label: '$$', color: 'text-brand' },
  premium: { label: '$$$', color: 'text-warning' },
};

const ModelSelector: React.FC<{ provider: AIProvider }> = ({ provider }) => {
  const { t } = useT();
  const config = PROVIDERS[provider];
  const [selectedModel, setSelectedModel] = useState(apiKeyService.getSelectedModel(provider));

  useEffect(() => {
    setSelectedModel(apiKeyService.getSelectedModel(provider));
  }, [provider]);

  const handleSelect = (modelId: string) => {
    setSelectedModel(modelId);
    apiKeyService.setSelectedModel(modelId, provider);
  };

  const currentModel = config.models.find(m => m.id === selectedModel) || config.models[0];

  return (
    <div className="p-5 bg-surface-2 border border-border rounded-xl">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[15px] font-semibold text-text-primary">{t('admin.model')}</h3>
        <span className={cn('text-[11px] font-mono', tierBadge[currentModel.tier].color)}>
          {tierBadge[currentModel.tier].label}
        </span>
      </div>
      <select
        value={selectedModel}
        onChange={(e) => handleSelect(e.target.value)}
        className="w-full py-2.5 px-3 bg-surface-1 border border-border rounded-lg text-text-primary text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 cursor-pointer appearance-none"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%237B7B88' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
      >
        {config.models.map(model => (
          <option key={model.id} value={model.id}>
            {model.name} — {model.description}
          </option>
        ))}
      </select>
      <p className="text-[11px] text-text-faint mt-2 m-0">{currentModel.description}</p>
    </div>
  );
};
